import React, { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  TextField,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid";
import { useDeclarationHistory } from "./useDeclarationHistory";
import { useDroughtStageUpdate } from "./useDroughtStageUpdate";

const columns = [
  {
    field: "declaration_ndx",
    headerName: "Declaration ID",
    width: 130,
  },
  {
    field: "zone_desc",
    headerName: "Zone",
    width: 200,
  },
  {
    field: "stage_label",
    headerName: "Stage",
    width: 150,
  },
  {
    field: "stage_code",
    headerName: "Stage Code",
    width: 100,
  },
  {
    field: "curtailment_pct",
    headerName: "Curtailment %",
    width: 120,
    type: "number",
    renderCell: ({ row }) => {
      const curtailment = +row.curtailment_pct || 0;
      let severity = "info";
      if (curtailment === 0) {
        severity = "success"; // blue
      } else if (curtailment === 10 || curtailment === 20) {
        severity = "warning"; // green
      } else if (curtailment >= 30) {
        severity = "error"; // red
      }

      return (
        <Chip
          label={`${curtailment}%`}
          color={severity}
          size="small"
          variant="outlined"
          width="100px"
          sx={{ width: "60px" }}
        />
      );
    },
  },
  {
    field: "effective_from",
    headerName: "Effective On",
    width: 130,
  },
  {
    field: "declared_by_user",
    headerName: "Declared By",
    width: 200,
    valueFormatter: (value) => {
      if (!value) return "Unknown";
      // Extract readable part from Auth0 user ID if possible
      return value.includes("|") ? value.split("|")[1] : value;
    },
  },
  {
    field: "created_dt",
    headerName: "Created",
    width: 150,
  },
  {
    field: "note",
    headerName: "Note",
    width: 250,
    flex: 1,
  },
];

export default function DroughtDeclarationHistory() {
  const [selectedZone, setSelectedZone] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const { zonesQuery } = useDroughtStageUpdate();

  const {
    data: allDeclarations,
    isLoading: declarationsLoading,
    isError: declarationsError,
  } = useDeclarationHistory();

  const filteredDeclarations = useMemo(() => {
    if (!allDeclarations) return [];

    let filtered = allDeclarations;

    // Filter by zone
    if (selectedZone) {
      filtered = filtered.filter(
        (declaration) => declaration.zone_ndx === parseInt(selectedZone, 10)
      );
    }

    // Filter by date range
    if (startDate || endDate) {
      filtered = filtered.filter((item) => {
        const itemDate = new Date(item.effective_from);
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;

        if (start && itemDate < start) return false;
        return !(end && itemDate > end);
      });
    }

    return filtered;
  }, [allDeclarations, selectedZone, startDate, endDate]);

  const handleZoneChange = (event) => {
    setSelectedZone(event.target.value);
  };

  const handleStartDateChange = (event) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
  };

  // Custom toolbar with filters and export
  const CustomToolbar = () => (
    <GridToolbarContainer
      sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}
    >
      <Grid container spacing={2} sx={{ flex: 1 }}>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Filter by Zone</InputLabel>
            <Select
              value={selectedZone}
              onChange={handleZoneChange}
              label="Filter by Zone"
              variant="outlined"
            >
              <MenuItem value="">
                <em>All Zones</em>
              </MenuItem>
              {zonesQuery.data?.map((zone) => (
                <MenuItem key={zone.zone_ndx} value={zone.zone_ndx}>
                  {zone.zone_desc}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Start Date"
            value={startDate}
            onChange={handleStartDateChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="End Date"
            value={endDate}
            onChange={handleEndDateChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>

      <GridToolbarExport
        slotProps={{
          button: {
            variant: "outlined",
            size: "small",
          },
        }}
      />
    </GridToolbarContainer>
  );

  if (zonesQuery.isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  if (zonesQuery.isError) {
    return <Alert severity="error">Failed to load drought zones data.</Alert>;
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Declaration History
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        View all past drought stage declarations with their effective dates and
        details.
      </Typography>

      {declarationsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load declaration history.
        </Alert>
      )}

      <Box sx={{ height: 400, width: "100%" }}>
        <DataGrid
          rows={filteredDeclarations}
          columns={columns}
          getRowId={(row) => `declaration-${row.declaration_ndx}`}
          loading={declarationsLoading}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          slots={{ toolbar: CustomToolbar }}
          sx={{
            "& .MuiDataGrid-cell": {
              fontSize: "0.875rem",
            },
          }}
        />
      </Box>
    </Paper>
  );
}
